import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword } from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc, updateDoc, arrayUnion, collection, addDoc, serverTimestamp } from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyCFeFHqqYhmnLhQjrw4NY8T4keNJUJD2jg",
  authDomain: "insightstock11172026.firebaseapp.com",
  projectId: "insightstock11172026",
  storageBucket: "insightstock11172026.firebasestorage.app",
  messagingSenderId: "1042863844737",
  appId: "1:1042863844737:web:039f0dbbe7bdd12a370c80",
  measurementId: "G-0M9EJ0R5MQ"
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const googleProvider = new GoogleAuthProvider();

// Google 登录
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // 检查用户是否已存在于 Firestore
    const userDoc = await getDoc(doc(db, "users", user.uid));

    let isNewUser = false;
    if (!userDoc.exists()) {
      // 新用户，创建记录
      isNewUser = true;
      await setDoc(doc(db, "users", user.uid), {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        tier: 'free',
        isPro: false,
        createdAt: new Date(),
        queryCount: 0,
        lastQueryDate: null,
        searchHistory: [],
        watchlist: []
      });
    }

    return { user, isNewUser };
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
};

// Email 注册
export const signUpWithEmail = async (email, password, displayName) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const user = result.user;

    // 创建用户记录
    await setDoc(doc(db, "users", user.uid), {
      email: user.email,
      displayName: displayName || email.split('@')[0],
      photoURL: null,
      tier: 'free',
      isPro: false,
      createdAt: new Date(),
      queryCount: 0,
      lastQueryDate: null,
      searchHistory: [],
      watchlist: []
    });

    return user;
  } catch (error) {
    console.error("Sign up error:", error);
    throw error;
  }
};

// Email 登录
export const signInWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error("Sign in error:", error);
    throw error;
  }
};

// 登出
export const logOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Logout error:", error);
  }
};

// 获取用户数据
export const getUserData = async (uid) => {
  const userDoc = await getDoc(doc(db, "users", uid));
  return userDoc.exists() ? userDoc.data() : null;
};

// Access check — registered users get free access to most features
// Portfolio still requires paid subscription (checked separately)
export const getTrialInfo = (userData) => {
  if (!userData) return { hasAccess: false, trialActive: false, daysLeft: 0, isPaid: false };

  const tier = userData.tier || 'free';
  const isPaid = tier !== 'free' || userData.isPro;

  // All registered users have access (free model)
  return { hasAccess: true, trialActive: false, daysLeft: 0, isPaid };
};

// 更新查询次数
export const updateQueryCount = async (uid) => {
  const userRef = doc(db, "users", uid);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists()) {
    const data = userDoc.data();
    const today = new Date().toDateString();

    if (data.lastQueryDate === today) {
      await setDoc(userRef, { ...data, queryCount: data.queryCount + 1 }, { merge: true });
      return data.queryCount + 1;
    } else {
      await setDoc(userRef, { ...data, queryCount: 1, lastQueryDate: today }, { merge: true });
      return 1;
    }
  }
  return 0;
};

// 检查查询限制
export const checkQueryLimit = async (uid) => {
  const userDoc = await getDoc(doc(db, "users", uid));

  if (userDoc.exists()) {
    const data = userDoc.data();
    const today = new Date().toDateString();

    // 根据 tier 确定限额
    const tier = data.tier || 'free';
    let limit = 5;
    if (tier === 'pro_monthly' || tier === 'pro_yearly') {
      limit = 50;
    } else if (tier === 'proplus_monthly' || tier === 'proplus_yearly') {
      limit = 100;
    } else if (data.isPro) {
      limit = 50;
    }

    const isPro = tier !== 'free' || data.isPro;
    const isProPlus = tier === 'proplus_monthly' || tier === 'proplus_yearly';

    if (data.lastQueryDate !== today) {
      return { allowed: true, remaining: limit, limit, isPro, isProPlus, tier };
    }

    const remaining = limit - data.queryCount;
    return { allowed: remaining > 0, remaining: Math.max(0, remaining), limit, isPro, isProPlus, tier };
  }

  return { allowed: false, remaining: 0, limit: 5, isPro: false, isProPlus: false, tier: 'free' };
};

// ========== 搜索历史功能 ==========

// 添加搜索历史
export const addSearchHistory = async (uid, ticker, priceAtSearch = null) => {
  try {
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const data = userDoc.data();
      let history = data.searchHistory || [];

      // 移除已存在的相同 ticker（避免重复）
      history = history.filter(item => item.ticker !== ticker);

      // 添加新搜索到开头 (include price if available)
      const entry = {
        ticker: ticker,
        searchedAt: new Date().toISOString()
      };
      if (priceAtSearch !== null && priceAtSearch !== undefined) {
        entry.priceAtSearch = priceAtSearch;
      }
      history.unshift(entry);

      // 只保留最近 20 条记录
      history = history.slice(0, 20);

      await updateDoc(userRef, { searchHistory: history });
      return history;
    }
    return [];
  } catch (error) {
    console.error("Add search history error:", error);
    return [];
  }
};

// 获取搜索历史
export const getSearchHistory = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, "users", uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return data.searchHistory || [];
    }
    return [];
  } catch (error) {
    console.error("Get search history error:", error);
    return [];
  }
};

// 清除搜索历史
export const clearSearchHistory = async (uid) => {
  try {
    const userRef = doc(db, "users", uid);
    await updateDoc(userRef, { searchHistory: [] });
    return true;
  } catch (error) {
    console.error("Clear search history error:", error);
    return false;
  }
};

// ========== Portfolio 历史功能 ==========

// 添加 Portfolio 分析历史
export const addPortfolioHistory = async (uid, portfolioData) => {
  try {
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const data = userDoc.data();
      let history = data.portfolioHistory || [];

      history.unshift({
        fileName: portfolioData.fileName,
        symbols: portfolioData.symbols || [],
        totalTrades: portfolioData.totalTrades || 0,
        netFlow: portfolioData.netFlow || 0,
        analyzedAt: new Date().toISOString()
      });

      history = history.slice(0, 10);

      await setDoc(userRef, { portfolioHistory: history }, { merge: true });
      return history;
    }
    return [];
  } catch (error) {
    console.error("Add portfolio history error:", error);
    return [];
  }
};

// 获取 Portfolio 历史
export const getPortfolioHistory = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, "users", uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return data.portfolioHistory || [];
    }
    return [];
  } catch (error) {
    console.error("Get portfolio history error:", error);
    return [];
  }
};

// 清除 Portfolio 历史
export const clearPortfolioHistory = async (uid) => {
  try {
    const userRef = doc(db, "users", uid);
    await setDoc(userRef, { portfolioHistory: [] }, { merge: true });
    return true;
  } catch (error) {
    console.error("Clear portfolio history error:", error);
    return false;
  }
};

// ========== Watchlist 功能（带 Risk Snapshot）==========

// 添加到 Watchlist（带完整 risk snapshot）
export const addToWatchlist = async (uid, ticker, companyName, price, riskClassification = null) => {
  try {
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const data = userDoc.data();
      let watchlist = data.watchlist || [];

      // 检查是否已存在
      const exists = watchlist.some(item => item.ticker === ticker);
      if (exists) {
        return { success: false, message: 'Already in watchlist' };
      }

      // 添加新股票，包含 risk snapshot
      watchlist.unshift({
        ticker: ticker,
        companyName: companyName || ticker,
        priceAtAdd: price || null,
        // Risk snapshot - 用于未来对比
        riskLevel: riskClassification?.riskLevel || null,
        riskDriver: riskClassification?.basis || null,
        suitability: riskClassification?.suitability || null,
        // 时间戳
        addedAt: new Date().toISOString(),
        lastCheckedAt: new Date().toISOString()
      });

      // 最多保留 50 只
      watchlist = watchlist.slice(0, 50);

      await setDoc(userRef, { watchlist: watchlist }, { merge: true });
      return { success: true, watchlist };
    }
    return { success: false, message: 'User not found' };
  } catch (error) {
    console.error("Add to watchlist error:", error);
    return { success: false, message: error.message };
  }
};

// 更新 Watchlist 中的 Risk Snapshot（当用户再次查看时调用）
export const updateWatchlistRiskSnapshot = async (uid, ticker, riskClassification, currentPrice = null) => {
  try {
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const data = userDoc.data();
      let watchlist = data.watchlist || [];

      // 找到并更新
      watchlist = watchlist.map(item => {
        if (item.ticker === ticker) {
          return {
            ...item,
            // 更新 risk snapshot
            riskLevel: riskClassification?.riskLevel || item.riskLevel,
            riskDriver: riskClassification?.basis || item.riskDriver,
            suitability: riskClassification?.suitability || item.suitability,
            currentPrice: currentPrice || item.currentPrice,
            lastCheckedAt: new Date().toISOString()
          };
        }
        return item;
      });

      await setDoc(userRef, { watchlist: watchlist }, { merge: true });
      return { success: true };
    }
    return { success: false };
  } catch (error) {
    console.error("Update watchlist snapshot error:", error);
    return { success: false };
  }
};

// 从 Watchlist 移除
export const removeFromWatchlist = async (uid, ticker) => {
  try {
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const data = userDoc.data();
      let watchlist = data.watchlist || [];

      watchlist = watchlist.filter(item => item.ticker !== ticker);

      await setDoc(userRef, { watchlist: watchlist }, { merge: true });
      return { success: true, watchlist };
    }
    return { success: false };
  } catch (error) {
    console.error("Remove from watchlist error:", error);
    return { success: false };
  }
};

// 获取 Watchlist
export const getWatchlist = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, "users", uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return data.watchlist || [];
    }
    return [];
  } catch (error) {
    console.error("Get watchlist error:", error);
    return [];
  }
};

// 检查是否在 Watchlist 中
export const isInWatchlist = async (uid, ticker) => {
  try {
    const watchlist = await getWatchlist(uid);
    return watchlist.some(item => item.ticker === ticker);
  } catch (error) {
    console.error("Check watchlist error:", error);
    return false;
  }
};

// ========== Prediction / Market Sense 功能 ==========

// Save a prediction (one per user per ticker per date)
export const savePrediction = async (uid, ticker, predictionData) => {
  try {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const predRef = doc(db, "users", uid, "predictions", `${ticker}_${today}`);
    await setDoc(predRef, {
      ticker,
      date: today,
      tier: predictionData.tier,
      mood: predictionData.mood,
      aiTier: predictionData.aiTier || null,
      actualChange: null,
      userCorrect: null,
      aiCorrect: null,
      iqChange: 0,
      createdAt: new Date().toISOString(),
    });
    return { success: true };
  } catch (error) {
    console.error("Save prediction error:", error);
    return { success: false, message: error.message };
  }
};

// Get today's prediction for a ticker (check if already predicted)
export const getTodayPrediction = async (uid, ticker) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const predRef = doc(db, "users", uid, "predictions", `${ticker}_${today}`);
    const predDoc = await getDoc(predRef);
    if (predDoc.exists()) {
      return predDoc.data();
    }
    return null;
  } catch (error) {
    console.error("Get today prediction error:", error);
    return null;
  }
};

// Get prediction history for a ticker (recent N entries)
export const getPredictionHistory = async (uid, ticker, limit = 30) => {
  try {
    // We'll use the user doc to store aggregated IQ data
    // Individual predictions are in subcollection
    const { getDocs, query, where, orderBy, collection: col, limit: lim } = await import("firebase/firestore");
    const predsRef = col(db, "users", uid, "predictions");
    const q = query(predsRef, where("ticker", "==", ticker), orderBy("date", "desc"), lim(limit));
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data());
  } catch (error) {
    console.error("Get prediction history error:", error);
    return [];
  }
};

// Save / update Market Sense IQ data (aggregated per user)
export const saveMarketSenseData = async (uid, data) => {
  try {
    const msRef = doc(db, "users", uid, "marketSense", "stats");
    await setDoc(msRef, {
      iqScore: data.iqScore || 150,
      streak: data.streak || 0,
      bestStreak: data.bestStreak || 0,
      moodStats: data.moodStats || {},
      updatedAt: new Date().toISOString(),
    }, { merge: true });
    return { success: true };
  } catch (error) {
    console.error("Save market sense error:", error);
    return { success: false };
  }
};

// Save today's top picks for a user (array, locked per day)
export const saveTodayPick = async (uid, picks) => {
  try {
    const msRef = doc(db, "users", uid, "marketSense", "stats");
    const today = new Date().toISOString().split('T')[0];
    // Support both single pick (legacy) and array
    const picksArray = Array.isArray(picks) ? picks : [picks];
    await setDoc(msRef, { todayPicks: picksArray, todayPickDate: today }, { merge: true });
    return { success: true };
  } catch (error) {
    console.error("Save today pick error:", error);
    return { success: false };
  }
};

// Get today's saved picks for a user (returns array or null)
export const getTodayPick = async (uid) => {
  try {
    const msRef = doc(db, "users", uid, "marketSense", "stats");
    const msDoc = await getDoc(msRef);
    if (msDoc.exists()) {
      const data = msDoc.data();
      const today = new Date().toISOString().split('T')[0];
      if (data.todayPickDate === today) {
        // Support both old format (todayPick) and new format (todayPicks)
        if (data.todayPicks && data.todayPicks.length > 0) {
          return data.todayPicks;
        }
        if (data.todayPick) {
          return [data.todayPick];
        }
      }
    }
    return null;
  } catch (error) {
    console.error("Get today pick error:", error);
    return null;
  }
};

// Record a login day for pick allocation (call on each page load)
export const recordLoginDay = async (uid) => {
  try {
    const msRef = doc(db, "users", uid, "marketSense", "stats");
    const msDoc = await getDoc(msRef);
    const today = new Date().toISOString().split('T')[0];

    let loginDays = [];
    if (msDoc.exists()) {
      loginDays = msDoc.data().loginDays || [];
    }

    // Add today if not already recorded
    if (!loginDays.includes(today)) {
      loginDays.push(today);
    }

    // Keep only last 7 days
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const cutoff = sevenDaysAgo.toISOString().split('T')[0];
    loginDays = loginDays.filter(d => d >= cutoff);

    await setDoc(msRef, { loginDays }, { merge: true });
    return loginDays.length;
  } catch (error) {
    console.error("Record login day error:", error);
    return 0;
  }
};

// Get pick allowance based on user tier and login frequency
// Free: 1 pick/day (2 if 3+ login days in 7-day window)
// Pro/Pro+: 5 picks/day
export const getPickAllowance = async (uid, isPro, isProPlus) => {
  try {
    if (isPro || isProPlus) return 5;

    const msRef = doc(db, "users", uid, "marketSense", "stats");
    const msDoc = await getDoc(msRef);
    if (msDoc.exists()) {
      const loginDays = msDoc.data().loginDays || [];
      // Count days in last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const cutoff = sevenDaysAgo.toISOString().split('T')[0];
      const recentDays = loginDays.filter(d => d >= cutoff).length;
      if (recentDays >= 3) return 2;
    }
    return 1;
  } catch (error) {
    console.error("Get pick allowance error:", error);
    return 1;
  }
};

// Load Market Sense IQ data
export const getMarketSenseData = async (uid) => {
  try {
    const msRef = doc(db, "users", uid, "marketSense", "stats");
    const msDoc = await getDoc(msRef);
    if (msDoc.exists()) {
      const data = msDoc.data();
      // Migrate old scoring system (max 100) to new system (max 300, start 150)
      if (data.iqScore !== undefined && data.iqScore < 100) {
        data.iqScore = 150;
        await setDoc(msRef, { iqScore: 150 }, { merge: true });
      }
      return data;
    }
    return null;
  } catch (error) {
    console.error("Get market sense error:", error);
    return null;
  }
};

// ========== Cross-Stock Market Sense (Aggregate) ==========

// Get ALL predictions across all tickers (for My Market Sense page)
export const getAllPredictions = async (uid, limitCount = 50) => {
  try {
    const { getDocs, query, orderBy, collection: col, limit: lim } = await import("firebase/firestore");
    const predsRef = col(db, "users", uid, "predictions");
    const q = query(predsRef, orderBy("date", "desc"), lim(limitCount));
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data());
  } catch (error) {
    console.error("Get all predictions error:", error);
    return [];
  }
};

// Get unique tickers the user has predicted on
export const getPredictedTickers = async (uid) => {
  try {
    const { getDocs, collection: col } = await import("firebase/firestore");
    const predsRef = col(db, "users", uid, "predictions");
    const snap = await getDocs(predsRef);
    const tickers = new Set();
    snap.docs.forEach(d => {
      const data = d.data();
      if (data.ticker) tickers.add(data.ticker);
    });
    return Array.from(tickers);
  } catch (error) {
    console.error("Get predicted tickers error:", error);
    return [];
  }
};

// ========== Settlement (调用后端结算) ==========

const API_BASE = 'https://insightstock-api-358482645476.us-west2.run.app';

// Call backend to settle pending predictions for a user
export const settlePredictions = async (uid) => {
  try {
    const resp = await fetch(`${API_BASE}/api/settle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid }),
    });
    if (!resp.ok) {
      console.error("Settle API error:", resp.status);
      return { settled: 0, results: [] };
    }
    return await resp.json();
  } catch (error) {
    console.error("Settle predictions error:", error);
    return { settled: 0, results: [] };
  }
};

export { onAuthStateChanged };